// ApiSidebar.tsx
import React, { useMemo, useState } from "react";
import { Wrench, ChevronDown } from "lucide-react";
import { apiEndpoints, type ApiEndpoint } from "../apiDocsSchema";

type ApiSidebarProps = {
  selectedEndpoint: ApiEndpoint | null;
  onSelectEndpoint: (ep: ApiEndpoint) => void;
};

/** ========== Badge DEV (mesma ideia do LeftBar) ========== */
const DevBadge: React.FC = () => (
  <span
    title="Endpoint ainda não finalizado"
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      marginLeft: 6,
      borderRadius: 6,
      padding: "1px 4px",
      background: "rgba(255, 200, 0, 0.25)",
      border: "1px solid rgba(222, 137, 25, 0.6)",
      flexShrink: 0,
    }}
  >
    <Wrench size={12} style={{ color: "#DE891AFF" }} />
  </span>
);

const ApiSidebar: React.FC<ApiSidebarProps> = ({
  selectedEndpoint,
  onSelectEndpoint,
}) => {
  const groups = useMemo(() => {
    const map: Record<string, ApiEndpoint[]> = {};
    for (const ep of apiEndpoints) {
      const g = ep.group || "Geral";
      if (!map[g]) map[g] = [];
      map[g].push(ep);
    }
    return map;
  }, []);

  const groupNames = Object.keys(groups).sort();

  // inicia com TODOS os grupos colapsados
  const [collapsedGroups, setCollapsedGroups] = useState<
    Record<string, boolean>
  >(() =>
    groupNames.reduce((acc, g) => {
      acc[g] = true;
      return acc;
    }, {} as Record<string, boolean>)
  );

  const toggleGroup = (groupName: string) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  return (
    <aside
      className="api-sidebar-nav"
      style={{
        borderRight: "1px solid #e5e7eb",
        padding: "20px 20px 20px 20px",
        boxSizing: "border-box",
        background: "#f3f4f6",
        fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
        overflowY: "auto",
        height: "100vh",
      }}
    >
      {/* Estilos globais da sidebar */}
      <style>
        {`
          .api-sidebar-nav {
            scrollbar-width: thin;
            scrollbar-color: #7b7d7c transparent;
          }
          .api-sidebar-nav::-webkit-scrollbar {
            width: 6px;
          }
          .api-sidebar-nav::-webkit-scrollbar-track {
            background: transparent;
          }
          .api-sidebar-nav::-webkit-scrollbar-thumb {
            background-color: #7b7d7c;
            border-radius: 999px;
          }

          .api-sidebar-item-btn {
            border: none;
            background: transparent;
            border-radius: 8px;
            padding: 8px 10px;
            cursor: pointer;
            width: 100%;
            text-align: left;
            transition: background-color 120ms ease, transform 80ms ease,
              box-shadow 120ms ease;
          }

          .api-sidebar-item-btn:hover {
            background: #e5e7eb;
          }

          .api-sidebar-item-btn--active {
            background: #EBECECFF;
            box-shadow: inset 0px 0 0 #787B82FF;
          }
        `}
      </style>

      {/* Cabeçalho */}
      <div style={{ marginBottom: 12 }}>
        <div
          style={{
            fontSize: 18,
            fontWeight: 500,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "#4b5563",
          }}
        >
          EVA API
        </div>
        <div
          style={{
            fontSize: 11,
            color: "#9ca3af",
            textTransform: "uppercase",
            letterSpacing: "0.14em",
            marginTop: 4,
          }}
        >
          Documentação
        </div>
      </div>

      {/* Linha separadora */}
      <div
        style={{
          height: 1,
          width: "100%",
          background: "#d4d4d8",
          opacity: 0.7,
          marginBottom: 12,
        }}
      />

      <nav
        style={{
          fontSize: 14,
        }}
      >
        {groupNames.map((g) => {
          const isCollapsed = collapsedGroups[g] === true;

          return (
            <div key={g} style={{ marginBottom: 14 }}>
              {/* Linha do título do grupo + seta */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 6,
                  cursor: "pointer",
                }}
                onClick={() => toggleGroup(g)}
              >
                <div
                  style={{
                    fontWeight: 600,
                    color: "#a0a1a5",
                    fontSize: 11,
                    textTransform: "uppercase",
                    letterSpacing: "0.12em",
                  }}
                >
                  {g}
                </div>
                <ChevronDown
                  size={14}
                  style={{
                    color: "#9ca3af",
                    flexShrink: 0,
                    transform: isCollapsed ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 160ms ease",
                  }}
                />
              </div>

              {/* Wrapper animado de abrir/fechar */}
              <div
                style={{
                  overflow: "hidden",
                  maxHeight: isCollapsed ? 0 : 320, // altura alvo aproximada
                  opacity: isCollapsed ? 0 : 1,
                  transition: "max-height 180ms ease, opacity 180ms ease",
                }}
              >
                <ul
                  style={{
                    listStyle: "none",
                    paddingLeft: 0,
                    margin: 0,
                  }}
                >
                  {groups[g].map((ep) => {
                    const isActive =
                      selectedEndpoint &&
                      selectedEndpoint.method === ep.method &&
                      selectedEndpoint.path === ep.path;

                    return (
                      <li key={ep.method + ep.path} style={{ marginBottom: 4 }}>
                        <button
                          onClick={() => onSelectEndpoint(ep)}
                          className={
                            "api-sidebar-item-btn" +
                            (isActive ? " api-sidebar-item-btn--active" : "")
                          }
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            gap: 2,
                          }}
                        >
                          {/* Linha do título + badge */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              width: "100%",
                              gap: 6,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 13,
                                color: "#111827",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                                maxWidth: "100%",
                                fontWeight: 500,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                              }}
                            >
                              <span
                                style={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {ep.name}
                              </span>

                              {/* 👇 Ícone de manutenção quando developed === false */}
                              {ep.developed === false && <DevBadge />}
                            </span>
                          </div>

                          {/* Linha método + path */}
                          <span
                            style={{
                              fontSize: 11,
                              color: "#6b7280",
                              fontFamily: "monospace",
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                              maxWidth: "100%",
                            }}
                          >
                            {ep.method} {ep.path}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          );
        })}
      </nav>
    </aside>
  );
};

export default ApiSidebar;
